import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { Toaster } from "./components/ui/sonner";
import WelcomeOnboardingModal from "./components/onboarding/WelcomeOnboardingModal";
import SignatureNudge from "./components/onboarding/SignatureNudge";

// Pages
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Register from "./pages/Register";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import Pipeline from "./pages/Pipeline";
import OpportunityDetail from "./pages/OpportunityDetail";
import AccountDetail from "./pages/AccountDetail";
import Organizations from "./pages/Organizations";
import OrganizationDetail from "./pages/OrganizationDetail";
import Engagements from "./pages/Engagements";
import EngagementDetail from "./pages/EngagementDetail";
import Intake from "./pages/Intake";
import DeckIntake from "./pages/DeckIntake";
import DeckLibrary from "./pages/DeckLibrary";
import DeckDetail from "./pages/DeckDetail";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import Campaigns from "./pages/Campaigns";
import EmailDesigner from "./pages/EmailDesigner";
import EmailAnalytics from "./pages/EmailAnalytics";
import Emails from "./pages/Emails";
import MarketIntelligence from "./pages/MarketIntelligence";
import IntelligenceSettings from "./pages/IntelligenceSettings";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import SignatureSettings from "./pages/SignatureSettings";
import EmailSettings from "./pages/EmailSettings";
import PublishingSettings from "./pages/PublishingSettings";
import VisionDecks from "./pages/VisionDecks";
import VisionDeckEditor from "./pages/VisionDeckEditor";
import VisionDeckPreview from "./pages/VisionDeckPreview";
import Territories from "./pages/Territories";
import PublicDeckView from "./pages/PublicDeckView";
import DecisionMakerDiscovery from "./pages/DecisionMakerDiscovery";
import UserAccessManagement from "./pages/UserAccessManagement";
import IndustryArchitect from "./pages/IndustryArchitect";
import RouteIntelligence from "./pages/RouteIntelligence";
import BuildAndSendReview from "./pages/BuildAndSendReview";
import LeadSubmissions from "./pages/LeadSubmissions";
import AIUsageDashboard from "./pages/AIUsageDashboard";
import WorkspaceSettings from "./pages/WorkspaceSettings";
import MileageSettings from "./pages/MileageSettings";
import BuildInquiries from "./pages/admin/BuildInquiries";
import Pricing from "./pages/Pricing";
import CheckoutReturn from "./pages/CheckoutReturn";
import OpportunityCSVImport from "./pages/OpportunityCSVImport";
import AdminDataCleanup from "./pages/AdminDataCleanup";
import UnitEconomics from "./pages/UnitEconomics";
import AuthCallback from "./pages/AuthCallback";
import BillingSetup from "./pages/BillingSetup";
import MediaStudio from "./pages/MediaStudio";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route Component (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Smart root route — sends authenticated users to /dashboard,
// unauthenticated visitors to the public /pricing sales page.
function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  // Feb 2026 — unauthenticated visitors see the new marketing landing
  // page (Landing.jsx) at `/`. Previously they were redirected to /pricing.
  // Authenticated app screens are unchanged.
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - NO AUTH REQUIRED */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/pricing" element={<Pricing />} />
      {/* Emergent Google OAuth callback — receives #session_id=... in URL hash */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* OAuth billing-setup magic link from approval email */}
      <Route path="/billing/setup" element={<BillingSetup />} />
      
      {/* Public Vision Deck View - Shareable Link */}
      <Route path="/view/deck/:deckId" element={<PublicDeckView />} />

      {/* Stripe checkout return — auth required to see the poll status */}
      <Route path="/billing/return" element={<ProtectedRoute><CheckoutReturn /></ProtectedRoute>} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      <Route path="/media-studio" element={<ProtectedRoute><MediaStudio /></ProtectedRoute>} />
      
      <Route 
        path="/pipeline" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <Pipeline />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/opportunities/csv-import" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <OpportunityCSVImport />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/opportunity/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <OpportunityDetail />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/account/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <AccountDetail />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/organizations" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <Organizations />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/organizations/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <OrganizationDetail />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/engagements" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <Engagements />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/engagements/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <EngagementDetail />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/intake" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <Intake />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/deck-intake" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <DeckIntake />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/deck-library" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <DeckLibrary />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/deck-library/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <DeckDetail />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/contacts" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <Contacts />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/contacts/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <ContactDetail />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/campaigns" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <Campaigns />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/market-intelligence" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <MarketIntelligence />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/intelligence-settings" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <IntelligenceSettings />
          </ProtectedRoute>
        } 
      />

      {/* One-time master-admin cleanup tool — remove this route + page after use */}
      <Route 
        path="/admin/data-cleanup" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDataCleanup />
          </ProtectedRoute>
        } 
      />

      {/* Internal unit economics dashboard — admin only */}
      <Route 
        path="/admin/unit-economics" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UnitEconomics />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/email-designer" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <EmailDesigner />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/emails" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <Emails />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/email-analytics" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <EmailAnalytics />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/users" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Users />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserAccessManagement />
          </ProtectedRoute>
        } 
      />
      
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      
      <Route 
        path="/signature" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'creative_lead']}>
            <SignatureSettings />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/settings/email" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'creative_lead']}>
            <EmailSettings />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/settings/publishing" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'creative_lead']}>
            <PublishingSettings />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/settings/mileage"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'creative_lead']}>
            <MileageSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/build-inquiries"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <BuildInquiries />
          </ProtectedRoute>
        }
      />
      
      <Route 
        path="/vision-decks" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <VisionDecks />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/lead-submissions"
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <LeadSubmissions />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-usage"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AIUsageDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspace-settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <WorkspaceSettings />
          </ProtectedRoute>
        }
      />
      
      <Route 
        path="/vision-decks/:id/edit" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <VisionDeckEditor />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/vision-decks/:id/preview" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <VisionDeckPreview />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/territories" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <Territories />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/decision-maker-discovery" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <DecisionMakerDiscovery />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/industry-architect" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep', 'executive_viewer']}>
            <IndustryArchitect />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/route-intelligence" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <RouteIntelligence />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/build-and-send/review/:workflowId" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'sales_rep']}>
            <BuildAndSendReview />
          </ProtectedRoute>
        } 
      />

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<RootRedirect />} />

      {/* Feb 2026 — explicit alias so /landing always renders the new
          marketing hero, regardless of auth state or query string. This
          is the canonical marketing URL (e.g. app.davidhughesdesign.ca/landing). */}
      <Route path="/landing" element={<Landing />} />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OnboardingProvider>
          <AppRoutes />
          {/* Welcome modal: first-login only; gated by backend
              should_show_welcome_modal. Existing users never see it. */}
          <WelcomeOnboardingModal />
          {/* One-shot signature nudge — fires on first Industry Architect save. */}
          <SignatureNudge />
          <Toaster position="top-right" richColors />
        </OnboardingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
